// /Users/rd/CaseManageVue/src/router/index.js

import { createRouter, createWebHistory } from 'vue-router'
import { setupGuards } from './guards'
import { isChunkLoadError } from '@/utils/chunkLoadRecovery'
import {
  STUDENT_ACCESS_ROLES,
  ADMIN_ONLY,
  ADMIN_CM_AND_DISTRICT_ADMIN,
  GEN_ED_HOURS_VIEWERS,
  FEEDBACK_CREATORS,
  DISTRICT_DASHBOARD_ROLES,
  MEETING_CALENDAR_ROLES,
  CASELOAD_REPORT_ROLES
} from '@/config/roles'
// Lazy-load every view so each becomes its own chunk, fetched on demand. This
// keeps the initial bundle small — users only download the code for the route
// they actually visit.
const LoginView = () => import('@/views/LoginView.vue')
const AdminView = () => import('@/views/AdminView.vue')
const AideScheduleView = () => import('@/views/AideScheduleView.vue')
const AideScheduleMasterView = () => import('@/views/AideScheduleMasterView.vue')
const StudentsView = () => import('@/views/StudentsView.vue')
const AdminExtendedAccess = () => import('@/views/AdminExtendedAccess.vue')
const AdminExtendedAccessOverview = () => import('@/views/AdminExtendedAccessOverview.vue')
const FeedbackFormView = () => import('@/views/FeedbackFormView.vue')
const FeedbackManageView = () => import('@/views/FeedbackManageView.vue')
const GenEdHoursView = () => import('@/views/GenEdHoursView.vue')
const TestingCalendarView = () => import('@/views/TestingCalendarView.vue')
const TestingAccommodationsView = () => import('@/views/TestingAccommodationsView.vue')
const TestingAttendanceView = () => import('@/views/TestingAttendanceView.vue')
const DistrictDashboardView = () => import('@/views/DistrictDashboardView.vue')
const MyCaseloadView = () => import('@/views/MyCaseloadView.vue')
const MeetingCalendarView = () => import('@/views/MeetingCalendarView.vue')
const CaseloadReportView = () => import('@/views/CaseloadReportView.vue')
const ReferenceDocumentsView = () => import('@/views/ReferenceDocumentsView.vue')
const HelpView = () => import('@/views/HelpView.vue')
const UsageAnalyticsView = () => import('@/views/UsageAnalyticsView.vue')
const NotFoundView = () => import('@/views/NotFoundView.vue')

// Route meta layout flags (consumed by App.vue / AppLayout):
//   layout: 'bare'   → no global hamburger shell (pre-auth screens)
// All other authenticated routes get the global shell by default.
const routes = [
  {
    path: '/login',
    name: 'Login',
    component: LoginView,
    meta: {
      layout: 'bare'
    }
  },
  {
    // Role-aware home: the guard redirects district-tier users to /district and
    // everyone else to /students (see resolveHomeLocation in guards.js). The
    // component is a fallback that never actually renders.
    path: '/',
    name: 'Home',
    component: StudentsView,
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/students',
    name: 'Students',
    component: StudentsView,
    meta: {
      requiresAuth: true,
      allowedRoles: STUDENT_ACCESS_ROLES
    }
  },
  {
    path: '/admin',
    name: 'Admin',
    component: AdminView,
    meta: {
      requiresAuth: true,
      allowedRoles: ADMIN_ONLY
    }
  },
  {
    path: '/district',
    name: 'DistrictDashboard',
    component: DistrictDashboardView,
    meta: {
      requiresAuth: true,
      allowedRoles: DISTRICT_DASHBOARD_ROLES,
      schoolContext: false
    }
  },
  // Common typo: distict → district
  {
    path: '/distict',
    redirect: '/district'
  },
  {
    // District case manager landing: cross-school caseload. schoolContext:false
    // because it spans schools (no ?school= redirect). The guard sends
    // allSchools case managers here as their home.
    path: '/my-caseload',
    name: 'MyCaseload',
    component: MyCaseloadView,
    meta: {
      requiresAuth: true,
      allowedRoles: ['case_manager', 'case_manager_full', 'speech_case_manager'],
      schoolContext: false
    }
  },

  // Former standalone admin pages — each tool has exactly one home inside the
  // admin shell now, so old deep links redirect to the matching tab. (Extended
  // access stays a real route: case managers may use it but cannot open /admin.)
  {
    path: '/admin/time-table',
    redirect: { path: '/admin', query: { tab: 'time-table', category: 'app-settings' } }
  },
  {
    // Aide schedule editing moved out of the admin panel onto the master
    // schedule page — keep old deep links landing where editing now lives.
    path: '/admin/aide-schedule',
    redirect: { path: '/aide-schedule-master' }
  },
  {
    path: '/admin/backup-restore',
    redirect: { path: '/admin', query: { tab: 'backup-restore', category: 'monitoring' } }
  },
  {
    path: '/admin/extended-access',
    name: 'AdminExtendedAccess',
    component: AdminExtendedAccess,
    meta: {
      requiresAuth: true,
      allowedRoles: ADMIN_CM_AND_DISTRICT_ADMIN
    }
  },
  {
    path: '/admin/extended-access-overview',
    name: 'AdminExtendedAccessOverview',
    component: AdminExtendedAccessOverview,
    meta: {
      requiresAuth: true,
      allowedRoles: ADMIN_CM_AND_DISTRICT_ADMIN
    }
  },
  {
    path: '/aide-schedule',
    name: 'AideSchedule',
    component: AideScheduleView,
    meta: {
      requiresAuth: true,
      allowedRoles: ['paraeducator']
    }
  },
  {
    // Read-only view of a specific aide's schedule. Fine-grained access is
    // enforced inside the component via admin-configurable menu visibility.
    path: '/aide-schedule/:aideId',
    name: 'AideScheduleByAide',
    component: AideScheduleView,
    meta: {
      requiresAuth: true,
      allowedRoles: STUDENT_ACCESS_ROLES
    }
  },
  {
    path: '/aide-schedule-master',
    name: 'AideScheduleMaster',
    component: AideScheduleMasterView,
    meta: {
      requiresAuth: true,
      allowedRoles: STUDENT_ACCESS_ROLES
    }
  },
  {
    path: '/feedback-manage',
    name: 'FeedbackManage',
    component: FeedbackManageView,
    meta: {
      requiresAuth: true,
      allowedRoles: FEEDBACK_CREATORS
    }
  },
  {
    // IEP plan-review / reevaluation due-date overview. School-scoped — the
    // default schoolContext redirect applies when no school is active.
    path: '/meeting-calendar',
    name: 'MeetingCalendar',
    component: MeetingCalendarView,
    meta: {
      requiresAuth: true,
      allowedRoles: MEETING_CALENDAR_ROLES
    }
  },
  {
    path: '/my-evaluations',
    name: 'MyEvaluations',
    redirect: (to) => ({
      path: '/students',
      query: { ...to.query, scope: 'evaluations' }
    })
  },
  {
    // Printable per-case-manager roster (students, grade, plan, PR/RE dates,
    // caseload + reevaluation counts). School-scoped, SPED chair + admin tier.
    path: '/caseload-report',
    name: 'CaseloadReport',
    component: CaseloadReportView,
    meta: {
      requiresAuth: true,
      allowedRoles: CASELOAD_REPORT_ROLES
    }
  },
  {
    path: '/gen-ed-hours',
    name: 'GenEdHours',
    component: GenEdHoursView,
    meta: {
      requiresAuth: true,
      allowedRoles: GEN_ED_HOURS_VIEWERS
    }
  },
  {
    path: '/testing-calendar',
    name: 'TestingCalendar',
    component: TestingCalendarView,
    meta: {
      requiresAuth: true,
      allowedRoles: STUDENT_ACCESS_ROLES,
      allowTestingAccess: true
    }
  },
  {
    path: '/testing-accommodations',
    name: 'TestingAccommodations',
    component: TestingAccommodationsView,
    meta: {
      requiresAuth: true,
      allowedRoles: STUDENT_ACCESS_ROLES,
      allowTestingAccess: true
    }
  },
  {
    path: '/testing-attendance',
    name: 'TestingAttendance',
    component: TestingAttendanceView,
    meta: {
      requiresAuth: true,
      allowedRoles: STUDENT_ACCESS_ROLES,
      allowTestingAccess: true
    }
  },
  {
    path: '/feedback/:instanceId',
    name: 'FeedbackForm',
    component: FeedbackFormView,
    meta: {
      requiresAuth: true,
      allowedRoles: STUDENT_ACCESS_ROLES
    }
  },
  {
    // Staff-facing reference document library (files + links). Any valid role —
    // read-only here; add/remove stays in Admin → Reference Documents.
    path: '/reference-documents',
    name: 'ReferenceDocuments',
    component: ReferenceDocumentsView,
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/help',
    name: 'Help',
    component: HelpView,
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/help/:topicId',
    name: 'HelpTopic',
    component: HelpView,
    meta: {
      requiresAuth: true
    }
  },
  {
    // Pilot usage analytics — APP OWNER only (email allow-list, see
    // config/analytics.js). Deliberately absent from every nav menu; direct
    // URL only. No student data on this page by design.
    path: '/usage-analytics',
    name: 'UsageAnalytics',
    component: UsageAnalyticsView,
    meta: {
      requiresAuth: true,
      usageAnalyticsOwnerOnly: true,
      schoolContext: false
    }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: NotFoundView
  }
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes
})

// ─── STALE CHUNK RECOVERY ────────────────────────────────────────────────────
// Each deploy renames hashed chunks. A tab left open across a deploy 404s the
// next time it lazy-loads one. A failed *route* chunk has no view to show
// (for example a login redirect to /district), so reload once to pick up the
// new index.html. The sessionStorage guard stops a loop when the asset is
// actually missing.
//
// Do not listen for window "vite:preloadError". Vite fires that for every
// failed dynamic import, including in-page dialogs (Meeting, Edit, Email).
// preventDefault() there swallows the rejection and a reload wipes the page
// the user is on. Leaving the event alone lets import() reject: this
// router.onError reloads for navigations, and the dialog loader retries then
// shows an error.
const RELOAD_GUARD_KEY = 'cmv_chunk_reload_at'

function reloadForStaleChunk(targetPath) {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) || 0)
    // Only auto-reload once per 10s window to avoid loops on a genuinely broken asset.
    if (Date.now() - last < 10000) return false
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()))
  } catch {
    /* sessionStorage unavailable → still attempt a single reload below */
  }
  const dest = targetPath || window.location.pathname + window.location.search
  window.location.assign(dest)
  return true
}

router.onError((error, to) => {
  if (isChunkLoadError(error)) {
    console.warn('Stale route chunk detected — reloading to fetch the latest build.')
    reloadForStaleChunk(to?.fullPath)
  }
})

// Set up the navigation guards
setupGuards(router)

export default router
